import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import PayRailModal from "@/components/PayRailModal";

const SERVICES = [
  {
    id: "feasibility_audit",
    index: "01",
    name: "Wallet Feasibility Audit",
    price: "$250",
    amount: 250,
    blurb: "A 48-hour deep dive to determine if funds are reachable. Delivered as a privileged memorandum with attack-surface scoring.",
    bullets: [
      "Chain-indexed address profile",
      "Key-derivation feasibility score (0–100)",
      "Recovery attack-surface map",
      "Privileged memo, eyes-only",
    ],
    cta: "Retain Audit — $250",
  },
  {
    id: "asset_trace",
    index: "02",
    name: "Asset Trace Report",
    price: "$2,500",
    amount: 2500,
    blurb: "Court-ready forensic map of asset movement across exchanges, mixers, and bridges. Suitable for private investigation, family-law discovery, and fraud recovery.",
    bullets: [
      "End-to-end flow graph (PDF + CSV)",
      "Mixer / bridge demixing analysis",
      "Exchange attribution & KYC pivots",
      "Technical declaration on request",
    ],
    cta: { primary: "Order Trace Report", secondary: "$2,500" },
  },
  {
    id: "recovery_execution",
    index: "03",
    name: "Recovery Execution",
    price: "Success-Based",
    blurb: "Technical reconstruction for lost keys, corrupted seed phrases, and legacy wallet formats. No recovery, no fee. Engagement only after a qualifying Feasibility Audit.",
    bullets: [
      "Partial-seed / passphrase reconstruction",
      "Legacy format resurrection",
      "Hardware & software forensics",
      "Escrowed key-handback protocol",
    ],
    cta: "Qualify via Feasibility Audit",
    gated: true,
  },
];

export default function Services() {
  const [modalSvc, setModalSvc] = useState(null);

  const onCta = (svc) => {
    if (svc.gated) {
      document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
      toast.info("Recovery Execution requires a qualifying Feasibility Audit first.");
      return;
    }
    setModalSvc(svc);
  };

  return (
    <section id="services" className="paper-section" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-4">
            <div className="mono-label-paper mb-6">§ 02 · SERVICES</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.1]">
              Fixed-fee engagements. Privileged deliverables.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6 pt-8">
            <blockquote className="text-paperink/85 text-base leading-relaxed border-l-2 border-bronze pl-6 italic font-serif-display">
              I&apos;m one of the few forensic experts that has developed Bitcoin code,
              starting from version 0.10 and the original Satoshi client. From finding
              TrueCrypt volumes in a 2011 backup drive, to matching zpub starting bytes
              to a zprv prefix sequence — my job is to discover what others cannot.
              All data is isolated, controlled, and encrypted.
            </blockquote>
            <div className="mt-4 mono-label-paper">— JOHN SAUDERTON · PRINCIPAL</div>
          </div>
        </div>

        {/* Service cards — separated, white surface, Amazon-orange primary CTAs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((svc) => (
            <div
              key={svc.id}
              className="bg-white border border-paperline p-10 flex flex-col shadow-sm hover:shadow-md transition-shadow relative"
              data-testid={`service-card-${svc.id}`}
            >
              <div className="flex items-start justify-between">
                <span className="mono-label-paper">TIER {svc.index}</span>
                <span className="mono-label-paper text-paperink/50">FIXED</span>
              </div>
              <h3 className="font-serif-display text-2xl mt-8 leading-tight">{svc.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif-display text-4xl">{svc.price}</span>
                {svc.id !== "recovery_execution" && <span className="mono-label-paper">USD</span>}
              </div>
              <p className="text-paperink/75 text-sm mt-6 leading-relaxed">{svc.blurb}</p>

              <ul className="mt-8 space-y-3 border-t border-paperline pt-6">
                {svc.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm">
                    <span className="w-3 h-px bg-paperink/40 mt-[0.7em] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 pt-6 border-t border-paperline">
                <button
                  onClick={() => onCta(svc)}
                  className={svc.gated ? "btn-graphite w-full" : "btn-amazon w-full"}
                  data-testid={`service-cta-${svc.id}`}
                >
                  {typeof svc.cta === "object" ? (
                    <span className="flex flex-col items-center leading-tight">
                      <span>{svc.cta.primary}</span>
                      <span className="mt-1 opacity-90 text-[10px]">{svc.cta.secondary}</span>
                    </span>
                  ) : (
                    svc.cta
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
          <span className="mono-label-paper">PAYMENT · VISA / MC / AMEX</span>
          <span className="mono-label-paper">· BITCOIN</span>
          <span className="mono-label-paper">· NDA ON REQUEST</span>
        </div>
      </div>

      {modalSvc && (
        <PayRailModal
          service={{ ...modalSvc, amount_display: modalSvc.price }}
          onClose={() => setModalSvc(null)}
        />
      )}
    </section>
  );
}
